import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, ButtonBase, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import type { TelemetryAttributes } from "../../instrumentation/gtm";

export type AvailabilityDotStatus =
  | "in-stock"
  | "low-stock"
  | "backorder"
  | "unavailable"
  | "unknown"
  | "unverified";

const AVAILABILITY_STATUS_CONFIG: Record<
  Exclude<AvailabilityDotStatus, "unverified">,
  { label: string; dotColor: string }
> = {
  "in-stock": { label: "In Stock", dotColor: "success.main" },
  "low-stock": { label: "Low Stock", dotColor: "warning.main" },
  backorder: { label: "Backorder", dotColor: "error.main" },
  unavailable: {
    label: "Contact Account Team for Availability",
    dotColor: "text.disabled",
  },
  unknown: { label: "Unknown Stock Status", dotColor: "text.disabled" },
};

/** Display label for a normalized status, e.g. for InventoryStatusChip's title-cased status prop. */
export function getAvailabilityStatusLabel(
  status: Exclude<AvailabilityDotStatus, "unverified">,
): string {
  return AVAILABILITY_STATUS_CONFIG[status].label;
}

export function getAvailabilityDotStatus(
  label: string | null | undefined,
): AvailabilityDotStatus {
  const normalizedLabel = label?.trim().toLowerCase();

  if (!normalizedLabel) {
    return "unknown";
  }

  if (normalizedLabel.includes("verify")) {
    return "unverified";
  }

  if (
    normalizedLabel.includes("not in stock") ||
    normalizedLabel.includes("none available") ||
    normalizedLabel.includes("out of stock") ||
    /\b(?:0|zero)\s+(?:units?\s+)?in stock\b/.test(normalizedLabel)
  ) {
    return "backorder";
  }

  if (normalizedLabel.includes("in stock") || normalizedLabel === "available") {
    return "in-stock";
  }

  if (normalizedLabel.includes("low") || normalizedLabel.includes("limited")) {
    return "low-stock";
  }

  if (
    normalizedLabel.includes("backorder") ||
    normalizedLabel.includes("back order")
  ) {
    return "backorder";
  }

  if (normalizedLabel.includes("contact account team")) {
    return "unavailable";
  }

  return "unknown";
}

export interface AvailabilityDotProps {
  status: AvailabilityDotStatus;
  label?: ReactNode;
  size?: number;
  verifying?: boolean;
  onVerify?: () => void;
  verifyingLabel?: ReactNode;
  buttonProps?: Omit<
    ComponentPropsWithoutRef<"button">,
    "children" | "onClick" | "disabled"
  > &
    Partial<TelemetryAttributes>;
  sx?: SxProps<Theme>;
}

export function AvailabilityDot({
  status,
  label,
  size = 8,
  verifying = false,
  onVerify,
  verifyingLabel = "Verifying...",
  buttonProps,
  sx,
}: AvailabilityDotProps) {
  const rootSx = [
    {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      flexShrink: 0,
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  if (status === "unverified") {
    return (
      <ButtonBase
        {...buttonProps}
        type="button"
        onClick={onVerify}
        disabled={verifying || !onVerify}
        sx={[
          ...rootSx,
          {
            justifyContent: "flex-start",
            color: "text.primary",
            textAlign: "left",
            "&.Mui-disabled": { color: "text.primary", opacity: 1 },
          },
        ]}
      >
        <RefreshIcon
          aria-hidden="true"
          data-testid="availability-verify-icon"
          data-spinning={verifying ? "true" : "false"}
          sx={{
            fontSize: size * 1.5,
            flexShrink: 0,
            animation: verifying
              ? "availability-dot-spin 1s linear infinite"
              : "none",
            "@keyframes availability-dot-spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
        <Typography
          component="span"
          variant="body2"
          color="text.primary"
          aria-live="polite"
        >
          {verifying ? verifyingLabel : (label ?? "Verify Stock")}
        </Typography>
      </ButtonBase>
    );
  }

  const config = AVAILABILITY_STATUS_CONFIG[status];

  return (
    <Box sx={rootSx}>
      <Box
        aria-hidden="true"
        sx={{
          width: size,
          height: size,
          borderRadius: "50%",
          bgcolor: config.dotColor,
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" color="text.primary">
        {label ?? config.label}
      </Typography>
    </Box>
  );
}